import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  groupSections,
  type KnowledgeSection,
  type KnowledgeView,
} from "@cloudox/api-client";
import { sectionSlug } from "../../lib/markdown";

/**
 * The left-rail section outline for one Knowledge View.
 *
 * A deliberately **light** outline (the selected view is already named by the
 * dropdown above it, so there is no "<View> sections" heading):
 *
 *  - Sections are clustered by their navigation `group`, but the group is shown
 *    only as a **thin divider + spacing** — never an uppercase label — so the
 *    rail stays scannable instead of stacking text layers.
 *  - A section with in-page subsections gets a chevron that expands a
 *    **subordinate** list (smaller, indented, muted, no background). Each
 *    subsection links to its heading anchor inside the section page.
 *  - The chevron is a separate control from the section link, so toggling
 *    subsections never hijacks normal section navigation.
 *  - The active section stays highlighted (and auto-expanded) even when a
 *    subsection within it is the active anchor.
 */
export interface SectionNavProps {
  readonly view: KnowledgeView;
  /** Key of the section currently open (the page the reader is on). */
  readonly currentSection?: string;
  /** Build the route for a top-level section. */
  readonly hrefFor: (sectionKey: string) => string;
  /**
   * Build the route + ``#anchor`` for an in-page subsection. Defaults to
   * ``hrefFor(sectionKey)#anchor`` when omitted.
   */
  readonly subHrefFor?: (sectionKey: string, anchor: string) => string;
  /** Called after any link is chosen (e.g. to close a mobile drawer). */
  readonly onNavigate?: () => void;
}

/**
 * Shorten a few section labels for the rail without changing the page title.
 * Keyed on the stable section **key** (never the display text) so it is robust
 * to wording changes. The full title ("Key Questions Answered") still renders
 * as the in-page heading.
 */
function navLabel(section: KnowledgeSection): string {
  if (section.key === "key-questions-answered") return "Key Questions";
  return section.title;
}

export function SectionNav({
  view,
  currentSection,
  hrefFor,
  subHrefFor,
  onNavigate,
}: SectionNavProps) {
  const location = useLocation();
  const activeAnchor = location.hash
    ? decodeURIComponent(location.hash.slice(1))
    : "";

  // Collapsed by default; the active section auto-expands so its subsections are
  // reachable. Users can still toggle any section independently.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() =>
    currentSection ? new Set([currentSection]) : new Set(),
  );
  useEffect(() => {
    if (!currentSection) return;
    setExpanded((prev) => {
      if (prev.has(currentSection)) return prev;
      const next = new Set(prev);
      next.add(currentSection);
      return next;
    });
  }, [currentSection]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const subHref =
    subHrefFor ??
    ((sectionKey: string, anchor: string) =>
      `${hrefFor(sectionKey)}#${anchor}`);

  const groups = groupSections(view.sections);

  return (
    <nav
      aria-label={`${view.displayName} sections`}
      className="mt-3 border-t pt-3"
      style={{ borderColor: "var(--border)" }}
    >
      {groups.map((group, groupIndex) => (
        <div key={group.group || "_ungrouped"}>
          {groupIndex > 0 ? (
            <div
              className="mx-2 my-2 border-t"
              style={{ borderColor: "var(--border)" }}
              role="separator"
            />
          ) : null}
          {group.sections.map((s) => {
            const isActive = s.key === currentSection;
            const hasSubs = s.subsections.length > 0;
            const isOpen = hasSubs && expanded.has(s.key);
            return (
              <div key={s.key}>
                <div className="flex items-center gap-0.5">
                  <Link
                    to={hrefFor(s.key)}
                    className="nav-item min-w-0 flex-1 text-[13px]"
                    aria-current={isActive ? "page" : undefined}
                    onClick={onNavigate}
                    // The rail truncates; the full title stays available on hover
                    // and, via the accessible name, to assistive tech.
                    title={s.title}
                    aria-label={s.title}
                  >
                    <span
                      className="min-w-0 flex-1 truncate"
                      style={{ opacity: s.empty ? 0.6 : 1 }}
                    >
                      {navLabel(s)}
                    </span>
                    {s.needsImprovement ? (
                      <span
                        aria-label="Needs improvement"
                        title="Knowledge quality: this section could be stronger"
                        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: "var(--warning)" }}
                      />
                    ) : null}
                  </Link>
                  {hasSubs ? (
                    <button
                      type="button"
                      onClick={() => toggle(s.key)}
                      aria-expanded={isOpen}
                      aria-label={
                        isOpen ? `Collapse ${s.title}` : `Expand ${s.title}`
                      }
                      className="shrink-0 rounded p-1"
                      style={{ color: "var(--fg-subtle)" }}
                    >
                      <Chevron open={isOpen} />
                    </button>
                  ) : (
                    <span className="w-6 shrink-0" aria-hidden />
                  )}
                </div>
                {isOpen ? (
                  <div
                    className="mb-1 ml-3 flex flex-col border-l"
                    style={{ borderColor: "var(--border)" }}
                  >
                    {s.subsections.map((sub) => {
                      const anchor = sectionSlug(sub);
                      const subActive = isActive && activeAnchor === anchor;
                      return (
                        <Link
                          key={sub}
                          to={subHref(s.key, anchor)}
                          onClick={onNavigate}
                          className="min-w-0 truncate py-1 pl-3 pr-2 text-[12px]"
                          title={sub}
                          aria-current={subActive ? "location" : undefined}
                          style={{
                            color: subActive ? "var(--fg)" : "var(--fg-subtle)",
                            fontWeight: subActive ? 500 : 400,
                          }}
                        >
                          {sub}
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function Chevron({ open }: { readonly open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden
      style={{
        transform: open ? "rotate(90deg)" : "none",
        transition: "transform 120ms",
      }}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
